import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import BookTable from './components/BookTable';
import BookEditModal from './components/BookEditModal';
import BulkUploadPanel from './components/BulkUploadPanel';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import CategoryManager from './components/CategoryManager';

const BookManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckDone, setAdminCheckDone] = useState(false);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);

  // Modal state
  const [editingBook, setEditingBook] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [booksToDelete, setBooksToDelete] = useState([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Operation state
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) {
        setAdminCheckDone(true);
        return;
      }
      try {
        const { data: { user: authUser } } = await supabase?.auth?.getUser();
        const role = authUser?.user_metadata?.role || authUser?.app_metadata?.role;
        setIsAdmin(role === 'admin');
      } catch (err) {
        console.error('Admin check error:', err);
        setIsAdmin(false);
      } finally {
        setAdminCheckDone(true);
      }
    };
    if (!authLoading) checkAdmin();
  }, [user, authLoading]);

  // Redirect non-admins
  useEffect(() => {
    if (adminCheckDone && !authLoading) {
      if (!user) {
        navigate('/sign-in');
      } else if (!isAdmin) {
        navigate('/e-book-catalog');
      }
    }
  }, [adminCheckDone, user, isAdmin, authLoading, navigate]);

  // Fetch books
  const fetchBooks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase?.from('ebooks')?.select('*')?.order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setBooks(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load books');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchBooks();
  }, [isAdmin, fetchBooks]);

  // Filter books
  useEffect(() => {
    let result = [...books];
    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      result = result?.filter(
        (b) =>
          b?.title?.toLowerCase()?.includes(q) ||
          b?.author?.toLowerCase()?.includes(q) ||
          b?.category?.toLowerCase()?.includes(q)
      );
    }
    if (categoryFilter !== 'all') {
      result = result?.filter((b) => b?.category === categoryFilter);
    }
    setFilteredBooks(result);
  }, [books, searchQuery, categoryFilter]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // CRUD operations
  const handleSaveBook = async (formData) => {
    setIsSaving(true);
    try {
      if (editingBook?.id) {
        const { error: updateError } = await supabase?.from('ebooks')?.update({
            ...formData,
            updated_at: new Date()?.toISOString(),
          })?.eq('id', editingBook?.id);
        if (updateError) throw updateError;
        showSuccess('Book updated successfully');
      } else {
        const { error: insertError } = await supabase?.from('ebooks')?.insert([{ ...formData }]);
        if (insertError) throw insertError;
        showSuccess('Book added successfully');
      }
      setShowEditModal(false);
      setEditingBook(null);
      await fetchBooks();
    } catch (err) {
      setError(err?.message || 'Failed to save book');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const ids = booksToDelete?.map((b) => b?.id);
      const { error: deleteError } = await supabase?.from('ebooks')?.delete()?.in('id', ids);
      if (deleteError) throw deleteError;
      showSuccess(`${ids?.length} book(s) deleted successfully`);
      setShowDeleteModal(false);
      setBooksToDelete([]);
      setSelectedIds([]);
      await fetchBooks();
    } catch (err) {
      setError(err?.message || 'Failed to delete books');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkUpload = async (booksData) => {
    setIsUploading(true);
    try {
      const { error: insertError } = await supabase?.from('ebooks')?.insert(booksData);
      if (insertError) throw insertError;
    } catch (err) {
      setError(err?.message || 'Bulk upload failed');
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  const handleBulkUploadComplete = async (booksData) => {
    await handleBulkUpload(booksData);
    showSuccess(`Books imported successfully`);
    setShowBulkUpload(false);
    await fetchBooks();
  };

  const handleBulkCategoryUpdate = async (ids, category) => {
    const { error: updateError } = await supabase?.from('ebooks')?.update({ category, updated_at: new Date()?.toISOString() })?.in('id', ids);
    if (updateError) throw updateError;
    await fetchBooks();
  };

  // Selection handlers
  const handleBulkSelect = (id, checked) => {
    setSelectedIds((prev) => checked ? [...prev, id] : prev?.filter((sid) => sid !== id));
  };

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? filteredBooks?.map((b) => b?.id) : []);
  };

  const handleBulkDelete = () => {
    const toDelete = books?.filter((b) => selectedIds?.includes(b?.id));
    setBooksToDelete(toDelete);
    setShowDeleteModal(true);
  };

  const categories = ['all', ...new Set(books?.map((b) => b?.category)?.filter(Boolean))];

  // Loading / auth states
  if (authLoading || !adminCheckDone) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Icon name="Loader2" size={40} className="animate-spin text-primary" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) return null;

  return (
    <>
      <Helmet>
        <title>Book Management | StrategyShelf Admin</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Admin Header */}
        <header className="bg-card border-b border-border shadow-sm sticky top-0 z-40">
          <div className="max-w-[1536px] mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/e-book-catalog')}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Back to catalog"
              >
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm hidden sm:inline">Back to Catalog</span>
              </button>
              <div className="h-5 w-px bg-border" />
              <div className="flex items-center gap-2">
                <Icon name="BookOpen" size={22} className="text-primary" />
                <h1 className="text-lg font-semibold text-foreground">Book Management</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                <Icon name="Shield" size={12} />
                Admin
              </span>
            </div>
          </div>
        </header>

        <main className="max-w-[1536px] mx-auto px-6 py-8">
          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2 text-success text-sm">
              <Icon name="CheckCircle" size={16} />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-error text-sm">
                <Icon name="AlertCircle" size={16} />
                {error}
              </div>
              <button onClick={() => setError(null)} className="text-error hover:opacity-70">
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {/* Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Books', value: books?.length, icon: 'BookOpen', color: 'text-primary' },
              { label: 'Categories', value: categories?.length - 1, icon: 'Tag', color: 'text-accent' },
              { label: 'Selected', value: selectedIds?.length, icon: 'CheckSquare', color: 'text-success' },
              { label: 'Filtered', value: filteredBooks?.length, icon: 'Filter', color: 'text-muted-foreground' },
            ]?.map((stat) => (
              <div key={stat?.label} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name={stat?.icon} size={16} className={stat?.color} />
                  <span className="text-xs text-muted-foreground">{stat?.label}</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="bg-card border border-border rounded-xl mb-4">
            <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 w-full sm:max-w-xs">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search books..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-9"
                />
              </div>

              {/* Category Filter */}
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e?.target?.value)}
                className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                aria-label="Filter by category"
              >
                {categories?.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat?.charAt(0)?.toUpperCase() + cat?.slice(1)}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2 ml-auto">
                {/* Bulk Actions */}
                {selectedIds?.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setShowCategoryManager(true)}
                      className="text-sm"
                    >
                      <Icon name="Tag" size={16} className="mr-1" />
                      Reassign Category
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleBulkDelete}
                      className="text-sm border-error text-error hover:bg-error/10"
                    >
                      <Icon name="Trash2" size={16} className="mr-1" />
                      Delete ({selectedIds?.length})
                    </Button>
                  </>
                )}

                <Button
                  variant="outline"
                  onClick={() => setShowBulkUpload(true)}
                  className="text-sm"
                >
                  <Icon name="Upload" size={16} className="mr-1" />
                  <span className="hidden sm:inline">Bulk Upload</span>
                </Button>

                <Button
                  onClick={() => { setEditingBook({}); setShowEditModal(true); }}
                  className="text-sm"
                >
                  <Icon name="Plus" size={16} className="mr-1" />
                  Add Book
                </Button>
              </div>
            </div>
          </div>

          {/* Book Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-3">
                  <Icon name="Loader2" size={36} className="animate-spin text-primary" />
                  <p className="text-muted-foreground text-sm">Loading books...</p>
                </div>
              </div>
            ) : (
              <BookTable
                books={filteredBooks}
                selectedIds={selectedIds}
                onEdit={(book) => { setEditingBook(book); setShowEditModal(true); }}
                onDelete={(book) => { setBooksToDelete([book]); setShowDeleteModal(true); }}
                onBulkSelect={handleBulkSelect}
                onSelectAll={handleSelectAll}
              />
            )}
          </div>

          {/* Footer info */}
          {!isLoading && (
            <p className="text-xs text-muted-foreground mt-3 text-right">
              Showing {filteredBooks?.length} of {books?.length} books
            </p>
          )}
        </main>
      </div>

      {/* Modals */}
      {showEditModal && (
        <BookEditModal
          book={editingBook}
          onSave={handleSaveBook}
          onClose={() => { setShowEditModal(false); setEditingBook(null); }}
          isSaving={isSaving}
        />
      )}

      {showBulkUpload && (
        <BulkUploadPanel
          onUpload={handleBulkUploadComplete}
          isUploading={isUploading}
          onClose={() => setShowBulkUpload(false)}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmModal
          books={booksToDelete}
          onConfirm={handleDeleteConfirm}
          onClose={() => { setShowDeleteModal(false); setBooksToDelete([]); }}
          isDeleting={isDeleting}
        />
      )}

      {showCategoryManager && (
        <CategoryManager
          books={books?.filter((b) => selectedIds?.includes(b?.id))}
          onBulkCategoryUpdate={handleBulkCategoryUpdate}
          onClose={() => setShowCategoryManager(false)}
        />
      )}
    </>
  );
};

export default BookManagement;
